import React, { useEffect, useCallback, useMemo } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  RefreshControl,
  TouchableOpacity,
  Text,
  ActivityIndicator,
} from 'react-native';
import { showAlert } from '@/utils/common/alert';
import Icon from 'react-native-vector-icons/Ionicons';
import { useAppDispatch, useAppSelector } from '@/store';
import { InvitationListItem } from './InvitationListItem';
import { EmptyState } from '@/components/common/residents/EmptyState';
import { Invitation } from '@/types/residents/home';
import { COLORS, PADDING } from '@/constants/residents/homeConstants';
import { invitationsStrings } from '@/constants/residents/strings';
import { acceptInvitation, declineInvitation, fetchInvitations } from '@/store/slices/residents/invitationsSlice';
import { fetchLinkedAccounts } from '@/store/slices/residents/homeSlice';
import { useResponsive } from '@/hooks/common/useResponsive';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { DesktopHeaderCard } from '@/components/residents/desktop/DesktopHeaderCard';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { UserType } from '@/types/residents/user';
import DesktopInviteFormModal from '@/components/residents/invitations/DesktopInviteFormModal';
import { useToast } from '@/contexts/admin/ToastContext';

interface InvitationsTabProps {
  onSendInvite: () => void;
}

export const InvitationsTab: React.FC<InvitationsTabProps> = ({ onSendInvite }) => {
  const dispatch = useAppDispatch();
  const { invitations, isLoading, error } = useAppSelector((state) => state.invitations);
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const { user } = useAppSelector((state) => state.auth);
  const isResident = user?.type === UserType.RESIDENT;
  const [showInviteModal, setShowInviteModal] = React.useState(false);
  const { showSuccess } = useToast();

  // Load invitations when component mounts
  useEffect(() => {
    dispatch(fetchInvitations());
  }, [dispatch]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    dispatch(fetchInvitations());
  }, [dispatch]);

  // Handle accept invitation
  const handleAcceptInvitation = useCallback(async (invitation: Invitation) => {
    try {
      await dispatch(acceptInvitation(invitation.id)).unwrap();
      // Success - invitation accepted and removed from list
      showSuccess('Invitation accepted');
      // Refresh linked accounts so the new connection appears immediately
      dispatch(fetchLinkedAccounts());
    } catch (error) {
      // Show error alert if accept fails
      showAlert(
        'Error',
        error instanceof Error ? error.message : 'Failed to accept invitation'
      );
    }
  }, [dispatch]);

  // Handle decline invitation
  const handleDeclineInvitation = useCallback(async (invitation: Invitation) => {
    try {
      await dispatch(declineInvitation({ invitationId: invitation.id, sender: invitation.sender })).unwrap();
      // Success - invitation declined and removed from list
    } catch (error) {
      // Show error alert if decline fails
      showAlert(
        'Error',
        error instanceof Error ? error.message : 'Failed to decline invitation'
      );
    }
  }, [dispatch]);

  // Render individual invitation item
  const renderInvitationItem = ({ item }: { item: Invitation }) => (
    <InvitationListItem 
      invitation={item} 
      onAccept={handleAcceptInvitation}
      onDecline={handleDeclineInvitation}
    />
  );

  // Render header with send invite button
  const renderListHeader = () => {
    if (isDesktop && isResident) {
      return (
        <View style={styles.desktopHeaderWrapper}>
          <DesktopHeaderCard
            icon={<RESIDENTS_SVG_COMPONENTS.SEND_INVITATION width={64} height={64} color={RESIDENTS_DESKTOP_COLORS.ICON_INACTIVE} />}
            title={invitationsStrings.sendInvite.title}
            subtitleLines={[invitationsStrings.sendInvite.description]}
            ctaText={invitationsStrings.sendInvite.cta}
            onPressCta={() => setShowInviteModal(true)}
          />
          {isLoading && (
            <View style={styles.desktopLoadingRow}>
              <ActivityIndicator size="small" color={RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY} />
            </View>
          )}
          {!isLoading && invitations.length > 0 && (
            <Text style={styles.desktopSectionHeading}>All Invitations</Text>
          )}
          <DesktopInviteFormModal visible={showInviteModal} onClose={() => setShowInviteModal(false)} />
        </View>
      );
    }

    return isResident ? (
      <View style={[styles.headerContainer, isDesktop && styles.headerContainerDesktop]}>
        <TouchableOpacity 
          style={styles.sendInviteCard}
          onPress={onSendInvite}
          accessibilityRole="button"
          accessibilityLabel={invitationsStrings.sendInvite.cta}
        >
          <View style={styles.sendInviteContent}>
            <View style={styles.iconContainer}>
              <Icon 
                name="mail-open-outline" 
                size={32}
                color={COLORS.circleIconPrimaryTint}
              />
            </View>
            <View style={styles.rightContentStack}>
              <View style={styles.labelStack}>
                <Text style={styles.headerTitle}>{invitationsStrings.sendInvite.title}</Text>
                <Text style={styles.headerDescription}>{invitationsStrings.sendInvite.description}</Text>
              </View>
              <TouchableOpacity style={styles.ctaButton} onPress={onSendInvite}>
                <Text style={styles.ctaButtonText}>{invitationsStrings.sendInvite.cta}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </View>
    ) : null;
  };

  // Render empty state - matches iOS emptyView
  const renderEmptyState = () => (
    <EmptyState
      title={invitationsStrings.empty.title}
      description={invitationsStrings.empty.description}
      iconName="mail-outline" // Matches iOS .Icon.emptyInbox
    />
  );

  return (
    <View style={[styles.container, isDesktop && styles.containerDesktop]}>
      {isLoading ? (
        <LoadingSpinner message="Loading invitations..." />
      ) : (
        <FlatList
          data={invitations}
          renderItem={renderInvitationItem}
          keyExtractor={(item) => item.id.toString()}
          style={[styles.list, isDesktop && styles.listDesktop]}
          contentContainerStyle={[
            styles.listContent,
            invitations.length === 0 && styles.emptyListContent
          ]}
          refreshControl={
            <RefreshControl
              refreshing={isLoading}
              onRefresh={handleRefresh}
              tintColor={COLORS.primary}
            />
          }
          ListHeaderComponent={renderListHeader}
          ListEmptyComponent={renderEmptyState}
          showsVerticalScrollIndicator={false}
          // iOS-like scroll behavior
          scrollEventThrottle={16}
          decelerationRate="normal"
        />
      )}
    </View>
  );
};
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.background, // iOS: .Background.primary
  },
  containerDesktop: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  list: {
    flex: 1,
  },
  listDesktop: {
  },
  listContent: {
    paddingBottom: PADDING.padding6,
  },
  emptyListContent: {
    flexGrow: 1,
  },
  headerContainerDesktop: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
  },
  // Desktop header styling
  desktopHeaderWrapper: {
    paddingHorizontal: 16,
    paddingBottom: 8,
    gap: 12,
  },
  desktopHeaderCard: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#2f2f2f',
    padding: 16,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  desktopHeaderTextCol: {
    flexDirection: 'column',
    flex: 1,
    marginRight: 12,
  },
  desktopHeaderTitle: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 18,
    fontWeight: '600',
  },
  desktopHeaderSubtitle: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontSize: 14,
    marginTop: 6,
  },
  desktopHeaderButton: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.BUTTON_PRIMARY,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  desktopHeaderButtonText: {
    color: '#fff',
    fontWeight: '700',
  },
  desktopLoadingRow: {
    alignItems: 'center',
  },
  desktopSectionHeading: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 20,
    fontWeight: '800',
    paddingHorizontal: 4,
    marginTop: 10,
    marginBottom: 18,
  },
  headerContainer: {
    paddingHorizontal: PADDING.padding4,
    paddingTop: PADDING.padding4,
    paddingBottom: PADDING.padding2,
  },
  sendInviteCard: {
    backgroundColor: COLORS.cardBackground,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: COLORS.cardBorder,
    padding: PADDING.padding4,
    shadowColor: COLORS.shadow,
    shadowOffset: { width: 1, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 4,
  },
  sendInviteContent: {
    flexDirection: 'row', // iOS: mainStackView.axis = .horizontal
    alignItems: 'flex-start', // iOS: mainStackView.alignment = .top
  },
  iconContainer: {
    width: 66, // iOS: pinWidthTo(66)
    height: 66, // iOS: pinAspectRatioTo(1.0)
    borderRadius: 33,
    backgroundColor: COLORS.circleIconBackground, // iOS: .CircleIcon.background
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: PADDING.padding5, // iOS: mainStackView.spacing = .Padding.padding5
  },
  rightContentStack: {
    flex: 1,
    flexDirection: 'column', // iOS: rightContentStackView.axis = .vertical
    alignItems: 'flex-start', // iOS: rightContentStackView.alignment = .leading
  },
  labelStack: {
    flexDirection: 'column', // iOS: labelStackView.axis = .vertical
    alignItems: 'flex-start',
    marginBottom: PADDING.padding4, // iOS: rightContentStackView.spacing = .Padding.padding4
  },
  headerTitle: {
    fontSize: 16, // iOS: .Connections.primary
    fontWeight: '600',
    color: COLORS.textPrimary, // iOS: .Text.primary
    marginBottom: 2,
  },
  headerDescription: {
    fontSize: 13, // iOS: .Connections.secondary
    fontWeight: '400',
    color: COLORS.textSubtitle, // iOS: .Text.subtitle
    lineHeight: 18,
    flexWrap: 'wrap', // iOS: numberOfLines = 0
  },
  ctaButton: {
    backgroundColor: COLORS.primary,
    width: 150, // iOS: widthAnchor.constraint(equalToConstant: 150)
    height: 40, // iOS: heightAnchor.constraint(equalToConstant: 40)
    borderRadius: 6, // iOS: .CornerRadius.radiusHalf
    justifyContent: 'center',
    alignItems: 'center',
  },
  ctaButtonText: {
    fontSize: 13, // iOS: .Connections.secondary
    fontWeight: '600',
    color: 'white',
  },
});
